import { ProductKeys, SidenavSubjects } from '@logz-pkg/enums';
import { Container, Divider, Flex, Group } from '@logz-ui/styleguide';
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { ISidenavItemConfig, dataHubItemConfig, homeItemConfig } from '../../Sidenav/item-configs';
import { GlobalSearch } from '../../Sidenav/Fixed/GlobalSearch/GlobalSearch';
import { HomeSidenavItem } from '../../Sidenav/Fixed/HomeDashboard';
import { FixedSidenavItem } from './Item';
import { SettingsItem } from './SettingsItem';
import { Minifier } from './Minifier';
import { rootNavItems } from './items-config';
import { appModeService } from 'services/identity/app-mode/app-mode.service';
import { integrationsItemConfig } from 'ui/components/App/Navigation/Sidenav/item-configs/integrations';
import { useNavigationDisplayCondition } from 'ui/components/App/Navigation/utils/use-navigaition-display-condition';
import { ExtendedLogos, Logo } from 'ui/components/shared/Logo/Logo';

interface IFixedSidenavItemsListProps {
  onNavItemHover: (SidenavItemConfig: ISidenavItemConfig) => void;
  currentProduct: ProductKeys;
  isHoveringOnMenu: boolean;
  isMinified: boolean;
  onNavItemLeave?(): void;
  onSpacerHoverChange?(hover: boolean): void;
}

const Spacer = ({ onMouseEnter, onMouseLeave }) => (
  <Flex
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    flex={1}
    width={'100%'}
    subject={SidenavSubjects.CloseMenu}
  />
); // occupies open space, effectively pushing Settings down

export const FixedNavItemList: FunctionComponent<IFixedSidenavItemsListProps> = ({
  onNavItemHover,
  onNavItemLeave,
  currentProduct,
  isHoveringOnMenu,
  isMinified,
  onSpacerHoverChange,
}) => {
  const [isMinifierDragging, setIsMinifierDragging] = useState(false);
  const [shouldShowMinifier, setShouldShowMinifier] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const wasDraggingRef = useRef(false);

  useEffect(() => {
    if (isMinifierDragging) {
      wasDraggingRef.current = true;
      setShouldShowMinifier(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else if (isHoveringOnMenu) {
      setShouldShowMinifier(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    } else if (wasDraggingRef.current && !isMinified) {
      timeoutRef.current = setTimeout(() => {
        setShouldShowMinifier(false);
        wasDraggingRef.current = false;
      }, 1000);
    } else {
      setShouldShowMinifier(false);
      wasDraggingRef.current = false;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isMinifierDragging, isHoveringOnMenu, isMinified]);

  const handleLogoClicked = () => appModeService.goToModeRootState();
  const [dataHubItems] = useNavigationDisplayCondition([dataHubItemConfig]);

  return (
    <Group alignItems={'center'} gap={1} vertical fullHeight fullWidth mx={isMinified ? '2px' : undefined}>
      <Group gap={2} vertical fullWidth alignItems="center">
        <Logo logo={ExtendedLogos.Logzio} alt={'Logz.io'} mini={isMinified} onClick={handleLogoClicked} />
        <GlobalSearch />
        <HomeSidenavItem navigationItem={homeItemConfig} selected={currentProduct === homeItemConfig.key} />
      </Group>
      <Container fullWidth px={isMinified ? 1 : 3}>
        <Divider color={'gray.700'} size={'m'} my={isMinified ? 4 : undefined} />
      </Container>
      <Group alignItems={'center'} gap={isMinified ? 4 : '2'} vertical fullHeight>
        {rootNavItems.map(navigationItem => (
          <FixedSidenavItem
            key={navigationItem.key}
            onHover={onNavItemHover}
            onLeave={onNavItemLeave ?? (() => {})}
            navigationItem={navigationItem}
            selected={currentProduct === navigationItem.key}
          />
        ))}
        {shouldShowMinifier && <Minifier onDragStateChange={setIsMinifierDragging} />}
        <Spacer
          onMouseEnter={() => onSpacerHoverChange && onSpacerHoverChange(true)}
          onMouseLeave={() => onSpacerHoverChange && onSpacerHoverChange(false)}
        />
        {dataHubItems?.length > 0 && (
          <FixedSidenavItem
            navigationItem={dataHubItemConfig}
            onHover={onNavItemHover}
            onLeave={onNavItemLeave ?? (() => {})}
            selected={currentProduct === ProductKeys.DataHub}
          />
        )}

        <FixedSidenavItem
          navigationItem={integrationsItemConfig}
          onHover={onNavItemHover}
          onLeave={onNavItemLeave ?? (() => {})}
          selected={currentProduct === ProductKeys.Integrations}
        />
        <SettingsItem
          onHover={onNavItemHover}
          onLeave={onNavItemLeave ?? (() => {})}
          selected={currentProduct === ProductKeys.Settings}
        />
      </Group>
    </Group>
  );
};
